import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  type Edge,
  type Node,
  type OnNodeDrag,
} from '@xyflow/react'
import { MousePointerClick } from 'lucide-react'

import StepDetailPanel from './StepDetailPanel'
import { EDITOR_NODE_TYPES } from './EditorNodes'
import { EDITOR_EDGE_TYPES } from './InsertStepEdge'
import {
  EDGE_PATH_STYLE,
  EDITOR_LAYOUT,
  STEP_DRAG_EXTENT,
  publishPosition,
  startPosition,
  stepIndexFromX,
  stepPosition,
} from './layout'
import {
  WorkflowFlowMember,
  WorkflowStepDraft,
  createStepDraft,
  duplicateReviewerIdsOf,
  initialsOf,
  stepHasIssues,
} from './types'

const START_NODE_ID = '__start__'
const PUBLISH_NODE_ID = '__publish__'
const MAX_AVATARS = 3

// minZoom keeps long flows readable instead of shrinking them to fit — past
// ~3 steps the chain overflows and the user pans, like any flow builder.
const FIT_VIEW_OPTIONS = { padding: 0.12, minZoom: 0.8, maxZoom: 1 }
const PRO_OPTIONS = { hideAttribution: false }

type FlowSelection = { kind: 'step'; key: string } | { kind: 'publish' } | null

export interface WorkflowFlowEditorProps {
  steps: WorkflowStepDraft[]
  onChange: (steps: WorkflowStepDraft[]) => void
  members: WorkflowFlowMember[]
  /** Name of the publish target, shown on the Publish node. The plan itself only
   *  stores a folder id, so the consumer resolves the name (see findFolderName). */
  publishFolderName?: string | null
  /** Rendered in the side panel when the Publish node is selected. Omitting it
   *  makes the Publish node display-only — which is what the submit-for-review
   *  dialog wants, since there the target comes from the template. */
  publishPanel?: React.ReactNode
  /** false = template mode: an unstaffed step is a valid skeleton, not an error
   *  (Freelo #31947103). Default true — the submit dialog keeps strict staffing. */
  requireReviewers?: boolean
}

/**
 * Visual, Shopify-Flow-style editor for a submittal review plan: `[Start] → [1]
 * → … → [Publish]` on a React Flow canvas, with the selected step's details in a
 * side panel.
 *
 * Purely controlled and query-free, exactly like the form-list editor it
 * replaces — props in, `onChange` out. Layout is DERIVED from step order (see
 * layout.ts), never stored: dragging a node only chooses a new slot, and the row
 * is recomputed and snapped on drop. Validation is shown but never blocks
 * `onChange`; the parent still decides submittability.
 */
const WorkflowFlowEditorCanvas: React.FC<WorkflowFlowEditorProps> = ({
  steps,
  onChange,
  members,
  publishFolderName,
  publishPanel,
  requireReviewers = true,
}) => {
  const { t, i18n } = useTranslation()
  const { fitView } = useReactFlow()
  const nodesInitialized = useNodesInitialized()

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const [selection, setSelection] = useState<FlowSelection>(null)
  // Bumped on every drop so the row re-lays-out even when the drag did not
  // change the order — without it the node would stay where it was released.
  const [layoutNonce, setLayoutNonce] = useState(0)

  const stepsRef = useRef(steps)
  stepsRef.current = steps
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const hasPublishPanel = !!publishPanel
  const duplicateReviewerIds = useMemo(() => duplicateReviewerIdsOf(steps), [steps])

  const handleInsert = useCallback((index: number) => {
    const step = createStepDraft()
    const next = [...stepsRef.current]
    next.splice(index, 0, step)
    onChangeRef.current(next)
    setSelection({ kind: 'step', key: step.key })
  }, [])

  // Node/edge objects are rebuilt from scratch whenever anything they display
  // changes. The effect below depends on STRING signatures rather than on the
  // `steps`/`members` arrays: both parents rebuild `members` inline on every
  // render, and an identity-based dependency would loop forever.
  const stepsSignature = useMemo(
    () => steps.map((s) => `${s.key}~${s.name}~${s.durationDays ?? ''}~${s.reviewerIds.join('.')}`).join('|'),
    [steps],
  )
  const membersSignature = useMemo(() => members.map((m) => `${m.id}:${m.name}`).join('|'), [members])
  const selectionSignature = selection ? (selection.kind === 'step' ? `step:${selection.key}` : 'publish') : ''

  useEffect(() => {
    const duplicates = duplicateReviewerIdsOf(steps)
    const nameById = new Map(members.map((m) => [m.id, m.name]))

    const nextNodes: Node[] = [
      {
        id: START_NODE_ID,
        type: 'start',
        position: startPosition(EDITOR_LAYOUT),
        data: {},
        draggable: false,
      },
    ]

    steps.forEach((step, index) => {
      nextNodes.push({
        id: step.key,
        type: 'step',
        position: stepPosition(EDITOR_LAYOUT, index),
        draggable: true,
        extent: STEP_DRAG_EXTENT,
        data: {
          index,
          name: step.name,
          durationDays: step.durationDays,
          reviewerInitials: step.reviewerIds.slice(0, MAX_AVATARS).map((id) => initialsOf(nameById.get(id))),
          reviewerOverflow: Math.max(0, step.reviewerIds.length - MAX_AVATARS),
          reviewerCount: step.reviewerIds.length,
          invalid: stepHasIssues(step, duplicates, requireReviewers),
          isSelected: selectionSignature === `step:${step.key}`,
        },
      })
    })

    nextNodes.push({
      id: PUBLISH_NODE_ID,
      type: 'publish',
      position: publishPosition(EDITOR_LAYOUT, steps.length),
      draggable: false,
      data: {
        folderName: publishFolderName ?? null,
        selectable: hasPublishPanel,
        isSelected: selectionSignature === 'publish',
      },
    })

    const chain = [START_NODE_ID, ...steps.map((s) => s.key), PUBLISH_NODE_ID]
    const nextEdges: Edge[] = []
    for (let i = 0; i < chain.length - 1; i += 1) {
      nextEdges.push({
        id: `${chain[i]}->${chain[i + 1]}`,
        source: chain[i],
        target: chain[i + 1],
        type: 'insert',
        style: EDGE_PATH_STYLE,
        selectable: false,
        focusable: false,
        deletable: false,
        data: { insertIndex: i, onInsert: handleInsert },
      })
    }

    setNodes(nextNodes)
    setEdges(nextEdges)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsSignature, membersSignature, selectionSignature, publishFolderName, hasPublishPanel, layoutNonce, i18n.language, requireReviewers])

  // Keep the whole chain in view as it grows or shrinks. `useNodesInitialized`
  // flips to false whenever a node without measured dimensions appears and back
  // to true once React Flow's ResizeObserver has measured it — the only moment
  // fitView can actually frame the chain. A bare timeout raced that
  // measurement and lost when the editor mounted with a prefilled plan (the
  // nodes state starts [] and the `fitView` prop burns its one shot on it).
  useEffect(() => {
    if (nodesInitialized) fitView(FIT_VIEW_OPTIONS)
  }, [nodesInitialized, steps.length, fitView])

  const handleNodeClick = (_event: React.MouseEvent, node: Node) => {
    if (node.type === 'step') setSelection({ kind: 'step', key: node.id })
    else if (node.type === 'publish' && hasPublishPanel) setSelection({ kind: 'publish' })
  }

  const handleNodeDragStop: OnNodeDrag = (_event, node) => {
    const from = steps.findIndex((s) => s.key === node.id)
    if (from >= 0) {
      const to = stepIndexFromX(EDITOR_LAYOUT, node.position.x, steps.length)
      if (to !== from) {
        const next = [...steps]
        const [moved] = next.splice(from, 1)
        next.splice(to, 0, moved)
        onChange(next)
      }
    }
    setLayoutNonce((n) => n + 1)
  }

  const selectedIndex = selection?.kind === 'step' ? steps.findIndex((s) => s.key === selection.key) : -1
  const selectedStep = selectedIndex >= 0 ? steps[selectedIndex] : null

  const patchSelected = (patch: Partial<WorkflowStepDraft>) => {
    onChange(steps.map((s, i) => (i === selectedIndex ? { ...s, ...patch } : s)))
  }

  const toggleSelectedReviewer = (memberId: string) => {
    if (!selectedStep) return
    patchSelected({
      reviewerIds: selectedStep.reviewerIds.includes(memberId)
        ? selectedStep.reviewerIds.filter((id) => id !== memberId)
        : [...selectedStep.reviewerIds, memberId],
    })
  }

  const moveSelected = (direction: 'left' | 'right') => {
    const target = direction === 'left' ? selectedIndex - 1 : selectedIndex + 1
    if (target < 0 || target >= steps.length) return
    const next = [...steps]
    ;[next[selectedIndex], next[target]] = [next[target], next[selectedIndex]]
    onChange(next)
  }

  const deleteSelected = () => {
    onChange(steps.filter((_, i) => i !== selectedIndex))
    setSelection(null)
  }

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="h-[420px] min-w-0 flex-1 overflow-hidden rounded-md border bg-muted/20">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={EDITOR_NODE_TYPES}
          edgeTypes={EDITOR_EDGE_TYPES}
          onNodeClick={handleNodeClick}
          onNodeDragStop={handleNodeDragStop}
          nodesConnectable={false}
          elementsSelectable={false}
          deleteKeyCode={null}
          zoomOnScroll={false}
          preventScrolling={false}
          minZoom={0.35}
          maxZoom={1.5}
          fitView
          fitViewOptions={FIT_VIEW_OPTIONS}
          proOptions={PRO_OPTIONS}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      <div className="shrink-0 rounded-md border p-3 lg:w-80">
        {selectedStep ? (
          <StepDetailPanel
            step={selectedStep}
            index={selectedIndex}
            total={steps.length}
            members={members}
            duplicateReviewerIds={duplicateReviewerIds}
            onPatch={patchSelected}
            onToggleReviewer={toggleSelectedReviewer}
            onMove={moveSelected}
            onDelete={deleteSelected}
            requireReviewers={requireReviewers}
          />
        ) : selection?.kind === 'publish' && publishPanel ? (
          publishPanel
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <MousePointerClick className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('Submittals_wf_flow_select_hint')}</p>
          </div>
        )}
      </div>
    </div>
  )
}

/** React Flow's imperative API (`fitView`) is only reachable from inside a
 *  provider, and each editor instance needs its own store. */
const WorkflowFlowEditor: React.FC<WorkflowFlowEditorProps> = (props) => (
  <ReactFlowProvider>
    <WorkflowFlowEditorCanvas {...props} />
  </ReactFlowProvider>
)

export default WorkflowFlowEditor
